import { DIV } from '../elements';
import { Setoid } from 'fp-ts/lib/Setoid';
import { ReactNode } from 'react';

const renderSelectItem =
    <T>(renderItem: (a: T) => ReactNode, add: (a: T) => void) =>
    (v: T) =>
        DIV(
            { className: 'checkbox__item', onClick: () => add(v) },
            DIV({ className: 'checkbox__box' }),
            renderItem(v),
        );

const renderSelectItemSelected =
    <T>(renderItem: (a: T) => ReactNode, remove: (a: T) => void) =>
    (v: T) =>
        DIV(
            { className: 'checkbox__item selected', onClick: () => remove(v) },
            DIV({ className: 'checkbox__box' }),
            renderItem(v),
        );

/**
 * Renders a checkbox-style multi-select list.
 *
 * Every entry in `list` is shown as a toggleable `checkbox__item`; clicking an
 * unselected entry calls `add`, clicking a selected one calls `remove`.
 * Membership is determined by comparing each entry against `selected` with `S`.
 *
 * Curried: configure it once, then call the returned function with the full
 * option list and the currently selected subset on each render.
 *
 * @param S Setoid used to test whether an entry is in `selected`
 * @param renderItem renders the label of each entry
 * @param add called with an entry when an unselected item is clicked
 * @param remove called with an entry when a selected item is clicked
 * @returns a function taking the option list and the selected subset
 */
export const renderMultiSelect =
    <T>(
        S: Setoid<T>,
        renderItem: (a: T) => ReactNode,
        add: (a: T) => void,
        remove: (a: T) => void,
    ) =>
    (list: Readonly<T[]>, selected: T[]) => {
        const mkItem = renderSelectItem<T>(renderItem, add);
        const mkSelected = renderSelectItemSelected<T>(renderItem, remove);
        const items = list.map(i => {
            if (selected.findIndex(item => S.equals(i, item)) >= 0) {
                return mkSelected(i);
            }
            return mkItem(i);
        });

        return DIV({ className: 'select-multi' }, ...items);
    };
